import React, { useMemo } from 'react';
import { eCommentType, prettyCommentsType } from 'types/order/comment/eCommentType';
import { externalCommentTypes } from 'types/order/comment/externalCommentTypes';
import { CommentLayout } from './CommentLayout';
import { INewComment } from './commentSchema';
import { CommentsTable } from './CommentsTable';
import { onEditCommentTypeSignature } from './EditCommentType';
import { NewComment } from './NewComment';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../types/shared/UUID';
import { checkForMutationSuccess } from '../../../API/checkForMutationSuccess';
import { convertToError } from '../../../API/BaseApi';
import { Show } from '../../shared/Show';
import { eCommentScope } from '../../../types/order/comment/eCommentScope';
import {
  useCreateCommentMutation,
  useEditCommentMutation,
  useListCommentsQuery,
} from '../../../API/order/comments/CommentsApi';
import { useHasPermissions } from '../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { ISelectOption } from '../../../ui-toolkit';

export const ExternalCommentsContainer: React.FC = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const editPermissions = useHasPermissions([ePermissions.CommentsEdit]);
  const { data: comments = [], error: queryError } = useListCommentsQuery({ orderId, scope: eCommentScope.external });
  const [createExternalComment, { error, isLoading, isSuccess, isError }] = useCreateCommentMutation();
  const [updateExternalComment] = useEditCommentMutation();

  const onSubmit = async ({ content, type }: INewComment<eCommentType>) => {
    const response = await createExternalComment({
      orderId,
      content,
      type,
      scope: eCommentScope.external,
    });
    checkForMutationSuccess(response, 'Comment Created Successfully!');
  };

  const onEditCommentType: onEditCommentTypeSignature = async ({ commentId, commentType }) => {
    const response = await updateExternalComment({
      orderId,
      commentId,
      type: commentType,
      scope: eCommentScope.external,
    });
    checkForMutationSuccess(response, 'Comment Updated Successfully!');
  };

  const options: ISelectOption<eCommentType>[] = useMemo(
    () =>
      externalCommentTypes.map((type) => ({
        value: type,
        label: prettyCommentsType[type],
      })),
    []
  );
  return (
    <CommentLayout pageError={queryError}>
      <Show when={editPermissions}>
        <NewComment
          onSubmit={onSubmit}
          onSubmitFetchStatus={{ fetching: isLoading, success: isSuccess, fetched: isSuccess && !isError }}
          onSubmitFetchError={convertToError(error)}
          commentTypeOptions={options}
        />
      </Show>
      <CommentsTable
        comments={comments}
        commentTypeOptions={options}
        onEditCommentType={editPermissions ? onEditCommentType : undefined}
      />
    </CommentLayout>
  );
};
